import { useState } from 'react';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Label } from '@/components/ui/label';
import { RadioGroup, RadioGroupItem } from '@/components/ui/radio-group';
import type { Lead } from '../types';

export type CopyMode = 'basic' | 'all';

interface CopyLeadDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  lead: Lead;
  onConfirm: (copiedLead: Lead) => void;
}

/**
 * Creates a copy of a lead with only basic information
 * Basic info: name, company, addresses, phone numbers, email
 */
function createBasicInfoCopy(lead: Lead): Lead {
  return {
    id: 0,
    // Name and company
    firstName: lead.firstName,
    lastName: lead.lastName,
    customerName: `${lead.firstName} ${lead.lastName}`,
    companyName: lead.companyName,

    // Contact Information
    homePhone: lead.homePhone,
    cellPhone: lead.cellPhone,
    workPhone: lead.workPhone,
    otherPhone: lead.otherPhone,
    homePhoneLabel: lead.homePhoneLabel,
    cellPhoneLabel: lead.cellPhoneLabel,
    workPhoneLabel: lead.workPhoneLabel,
    otherPhoneLabel: lead.otherPhoneLabel,
    email: lead.email,

    // Property Address
    address1: lead.address1,
    address2: lead.address2,
    city: lead.city,
    state: lead.state,
    zip: lead.zip,
    propertyAddress: lead.propertyAddress,

    // Billing Address
    hasSameAddress: lead.hasSameAddress,
    billingAddress1: lead.billingAddress1,
    billingAddress2: lead.billingAddress2,
    billingCity: lead.billingCity,
    billingState: lead.billingState,
    billingZip: lead.billingZip,

    // Required defaults for new lead
    status: 'Unassigned',
    commissionRate: null,
    createdAt: '',
    updatedAt: '',
  };
}

/**
 * Creates a full copy of a lead with all information
 */
function createFullCopy(lead: Lead): Lead {
  return {
    ...lead,
    id: 0,
    // Reset status for new lead
    status: 'Unassigned',
    // Clear job number since this is a new lead
    jobNumber: undefined,
    // Reset audit fields
    createdAt: '',
    updatedAt: '',
    createdBy: undefined,
    updatedBy: undefined,
  };
}

/**
 * Dialog for copying a lead with options for basic info only or all information
 */
export function CopyLeadDialog({
  open,
  onOpenChange,
  lead,
  onConfirm,
}: CopyLeadDialogProps) {
  const [copyMode, setCopyMode] = useState<CopyMode>('basic');

  const handleConfirm = () => {
    const copiedLead = copyMode === 'basic'
      ? createBasicInfoCopy(lead)
      : createFullCopy(lead);
    onConfirm(copiedLead);
    onOpenChange(false);
  };

  const handleCancel = () => {
    setCopyMode('basic');
    onOpenChange(false);
  };

  const handleOpenChange = (newOpen: boolean) => {
    if (!newOpen) {
      setCopyMode('basic');
    }
    onOpenChange(newOpen);
  };

  return (
    <Dialog open={open} onOpenChange={handleOpenChange}>
      <DialogContent className="sm:max-w-[450px]">
        <DialogHeader>
          <DialogTitle>Copy Lead</DialogTitle>
          <DialogDescription>
            Choose which information to copy from this lead.
          </DialogDescription>
        </DialogHeader>

        <div className="py-4">
          <RadioGroup
            value={copyMode}
            onValueChange={(value) => setCopyMode(value as CopyMode)}
            className="space-y-4"
          >
            <div className="flex items-start space-x-3">
              <RadioGroupItem value="basic" id="copy-basic" className="mt-1" />
              <div className="flex-1">
                <Label htmlFor="copy-basic" className="font-medium cursor-pointer">
                  Basic Info Only
                </Label>
                <p className="text-sm text-gray-500 mt-1">
                  Name, company, addresses, phone numbers, and email
                </p>
              </div>
            </div>

            <div className="flex items-start space-x-3">
              <RadioGroupItem value="all" id="copy-all" className="mt-1" />
              <div className="flex-1">
                <Label htmlFor="copy-all" className="font-medium cursor-pointer">
                  All Information
                </Label>
                <p className="text-sm text-gray-500 mt-1">
                  All lead details including insurance, damage info, tenant info, notes, and more
                </p>
              </div>
            </div>
          </RadioGroup>
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={handleCancel}>
            Cancel
          </Button>
          <Button variant="action" onClick={handleConfirm}>
            Copy
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
